
import { Habit, Member, PointsHistory } from '../types';
import { format, subDays, eachDayOfInterval, isAfter, getDay, parseISO } from 'date-fns';
import { generateUUID } from '../utils/uuid';
import { trimOldPointsHistory } from './pointsHistoryService';

/**
 * Checks if a specific date is a required check-in day for the habit.
 */
export const isRequiredDay = (habit: Habit, date: Date): boolean => {
    const dayOfWeek = getDay(date); // 0 = Sun, 1 = Mon...

    if (habit.frequency === 'daily') return true;

    if (habit.frequency === 'weekly') {
        return habit.targetDays.includes(dayOfWeek);
    }

    // Interval is harder without a reference start date. 
    // We assume 'createdAt' is the start, or we simplify interval logic.
    // For 'interval', standard implementation usually checks:
    // (date - startDate) % interval === 0
    if (habit.frequency === 'interval' && habit.intervalDays) {
        const start = parseISO(habit.createdAt);
        const diffTime = Math.abs(date.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays % habit.intervalDays === 0;
    }

    // Fallback for legacy types
    if (habit.frequency === 'workdays') return dayOfWeek >= 1 && dayOfWeek <= 5;

    return false;
};

/**
 * Calculates settlements for missed check-ins.
 * Returns updated member structure and habits.
 */
export const runDailySettlement = (
    member: Member,
    habits: Habit[],
    lastSettlementDateStr: string
): {
    updatedMember: Member;
    updatedHabits: Habit[];
    summary: string[];
    pointsHistory: PointsHistory[];
} => {

    const today = new Date();
    const yesterday = subDays(today, 1);
    // If last settlement was yesterday or today, nothing to do (assuming we settled yesterday fully)
    // We strictly settle up to "Yesterday". Today is still active.
    let startDate = parseISO(lastSettlementDateStr);
    startDate = new Date(startDate.setDate(startDate.getDate() + 1)); // Start from next day

    // Safety break: if start date is after yesterday, nothing to do
    if (isAfter(startDate, yesterday)) {
        return { updatedMember: member, updatedHabits: habits, summary: [], pointsHistory: [] };
    }

    // Limit lookback to reasonable time (e.g. 30 days) to prevent massive calcs on return
    const excessiveLookback = subDays(today, 30);
    if (isAfter(excessiveLookback, startDate)) {
        startDate = excessiveLookback;
    }

    let totalDeduction = 0;
    const summaryMessages: string[] = [];
    const pointsHistoryRecords: PointsHistory[] = [];

    // Helper to get formatted date key
    const getDateKey = (d: Date) => format(d, 'yyyy-MM-dd');

    const newHabits = habits.map(habit => {
        let currentHabit = { ...habit };
        
        // Check if habit is currently archived
        const isPermanent = currentHabit.isPermanent ?? true;
        const todayKey = getDateKey(today);
        const isArchived = !isPermanent && currentHabit.endDate && new Date(currentHabit.endDate) < today;
        
        if (isArchived) {
            // Skip archived habits - no point calculation
            return currentHabit;
        }
        
        let pointsLog = { ...(currentHabit.pointsLog || {}) };
        let completedDates = currentHabit.completedDates || [];
        let streak = currentHabit.streak;
        let basePoints = currentHabit.basePoints || 0;

        // Habit effective start date: use startDate if set, otherwise createdAt
        const habitStartDate = currentHabit.startDate
            ? new Date(currentHabit.startDate)
            : parseISO(currentHabit.createdAt);
        const habitStartDateKey = getDateKey(habitStartDate);

        // Skip dates before the habit's effective start date
        const effectiveRangeStart = isAfter(startDate, habitStartDate) ? startDate : habitStartDate;
        if (isAfter(effectiveRangeStart, yesterday)) {
            return currentHabit;
        }

        const habitRange = eachDayOfInterval({ start: effectiveRangeStart, end: yesterday });

        // We need to track consecutive misses locally during this iteration
        // But we also need to know misses BEFORE the window. 
        // Simplified: Just calculate misses within this window + immediate history if needed.
        // For "Consecutive > 3 days", we track a running counter.
        let consecutiveMisses = 0;

        // Check backwards to init consecutiveMisses, but don't go before habit start date
        for (let i = 1; i <= 3; i++) {
            const d = subDays(effectiveRangeStart, i);
            if (d < habitStartDate) break;
            const req = isRequiredDay(currentHabit, d);
            const done = completedDates.includes(getDateKey(d));
            if (req && !done) consecutiveMisses++;
            else if (req && done) { consecutiveMisses = 0; break; }
        }

        habitRange.forEach(date => {
            const dateKey = getDateKey(date);
            
            // Skip dates before restoration date (if habit was restored)
            if (currentHabit.restoredAt && dateKey < currentHabit.restoredAt) {
                return;
            }
            
            const required = isRequiredDay(currentHabit, date);
            const completed = completedDates.includes(dateKey);

            // We only penalize if it was REQUIRED and NOT COMPLETED
            // Also check if already processed (pointsLog has entry? or strict overwrite?)
            // We prefer overwrite to ensure consistency for "missed" status

            if (required) {
                if (!completed) {
                    consecutiveMisses++;
                    // Reset streak logic: If missed a required day, streak becomes 0 on that day
                    streak = 0;

                    // Calculate Penalty
                    let penalty = basePoints;
                    if (consecutiveMisses > 3) {
                        penalty = basePoints * 2;
                    }

                    // Log it (as negative)
                    // Only if not already deducted (idempotency check ideally, but here overwrite is safe for correction)
                    if (!pointsLog[dateKey]) {
                        pointsLog[dateKey] = -penalty;
                        totalDeduction += penalty;

                        // Create points history record
                        const historyRecord: PointsHistory = {
                            id: generateUUID(),
                            memberId: member.id,
                            amount: -penalty,
                            type: 'settlement',
                            habitId: habit.id,
                            habitName: habit.title,
                            date: dateKey,
                            timestamp: date.getTime(),
                            reason: `未打卡: ${habit.title}`
                        };
                        pointsHistoryRecords.push(historyRecord);
                    }
                } else {
                    // Completed
                    consecutiveMisses = 0;
                    // Streak logic is usually handled by the daily toggle, 
                    // but if we are re-validating history, we assume completed implies streak maintained.
                    // We won't touch 'streak' number for completed days here to avoid conflict with live logic,
                    // ONLY reset it on misses.
                }
            }
        });

        return {
            ...currentHabit,
            streak, // Updated streak (reset to 0 if missed recently)
            pointsLog
        };
    });

    const updatedMember = trimOldPointsHistory({
        ...member,
        totalPoints: Math.max(0, (member.totalPoints || 0) - totalDeduction),
        lastSettlementDate: getDateKey(yesterday),
        pointsHistory: [...(member.pointsHistory || []), ...pointsHistoryRecords]
    });

    if (totalDeduction > 0) {
        summaryMessages.push(`结算完成：因未打卡扣除了 ${totalDeduction} 积分`);
    }

    return { updatedMember, updatedHabits: newHabits, summary: summaryMessages, pointsHistory: pointsHistoryRecords };
};
